import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { Template } from '../types/Template';

const TemplatesPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // Fetch templates on component mount
  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      // TODO: Replace with actual API call
      const response = await fetch(`${process.env.REACT_APP_API_URL}/templates`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      } else {
        const error = await response.json();
        console.error('Failed to fetch templates:', error.message);
        setTemplates([]);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      // Use mock data on error
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewTemplate = () => {
    navigate('/form?createTemplate=true');
  };

  const handleUseTemplate = (template: Template) => {
    navigate(`/form/template/${template._id}`);
  };

  const handlePreviewTemplate = (template: Template) => {
    setSelectedTemplate(template);
    setShowPreview(true);
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (window.confirm('Er du sikker på at du vil slette denne templaten?')) {
      try {
        // TODO: Replace with actual API call
        const response = await fetch(`${process.env.REACT_APP_API_URL}/templates/${templateId}`, {
          method: 'DELETE',
          credentials: 'include'
        });
        
        if (response.ok) {
          setTemplates(templates.filter(t => t._id !== templateId));
        } else {
          const error = await response.json();
          alert(`Feil ved sletting av template: ${error.message}`);
        }
      } catch (error) {
        console.error('Error deleting template:', error);
        alert('Feil ved sletting av template');
      }
    }
  };

  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    `${template.createdBy.firstName} ${template.createdBy.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="templates-page">
        <Header />
        <div className="templates-content">
          <div className="container">
            <h1>Laster templates...</h1>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="templates-page">
      <Header />
      <div className="templates-content">
        <div className="container">
          <div className="templates-header">
            <h1>Mine Templates</h1>
            <button 
              className="btn btn-primary"
              onClick={handleCreateNewTemplate}
            >
              Lag ny template
            </button>
          </div>

          <div className="templates-search">
            <input
              type="text"
              placeholder="Søk i templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="templates-grid">
            {filteredTemplates.length === 0 ? (
              <div className="no-templates">
                <h3>Ingen templates funnet</h3>
                <p>Lag din første template ved å klikke på "Lag ny template"</p>
              </div>
            ) : (
              filteredTemplates.map(template => (
                <div key={template._id} className="template-card">
                  <div className="template-card-header">
                    <h3>{template.name}</h3>
                    <div className="template-actions">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handlePreviewTemplate(template)}
                      >
                        Forhåndsvis
                      </button>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleUseTemplate(template)}
                      >
                        Bruk
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteTemplate(template._id)}
                      >
                        Slett
                      </button>
                    </div>
                  </div>
                  <div className="template-card-info">
                    <p><strong>Opprettet av:</strong> {template.createdBy.firstName} {template.createdBy.lastName}</p>
                    <p><strong>Dato:</strong> {new Date(template.createdAt).toLocaleDateString('no-NO')}</p>
                    <p><strong>Avdeling:</strong> {template.department} • <strong>Brukt:</strong> {template.usageCount} ganger</p>
                    {template.description && <p><strong>Beskrivelse:</strong> {template.description}</p>}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && selectedTemplate && (
        <div className="modal-overlay" onClick={() => setShowPreview(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Forhåndsvisning: {selectedTemplate.name}</h2>
              <button 
                className="btn btn-close"
                onClick={() => setShowPreview(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="template-preview">
                <h4>Template Data:</h4>
                <pre>{JSON.stringify(selectedTemplate.formData, null, 2)}</pre>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowPreview(false)}
              >
                Lukk
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleUseTemplate(selectedTemplate)}
              >
                Bruk denne templaten
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplatesPage;